/**
 * NetworkManager — handles WebSocket connection, message sending, and message routing.
 */
export default class NetworkManager {
    constructor() {
        this.ws = null;
        this.handlers = {};  // type -> callback
        this.ping = 0;       // latest RTT in ms
        this.jitter = 0;     // estimated jitter in ms
        this._pingInterval = null;
        this._pingSentAt = 0;
        this._pingHistory = [];    // last N RTT samples for jitter calc
        this._maxPingHistory = 20;
        this._lastTickTime = 0;    // timestamp of last game_tick arrival
        this._tickIntervals = [];  // measured intervals between game_ticks
        this._maxTickHistory = 30;
        this._jitterReportTimer = 0;
        this._jitterReportInterval = 10000; // report to server every 10s

        // Reconnection state
        this._reconnecting = false;
        this._reconnectAttempt = 0;
        this._reconnectTimer = null;
        this._reconnectStartTime = 0;
        this._maxReconnectDuration = 5 * 60 * 1000; // 5 minutes
        this._playerName = null;
        this._intentionalClose = false;
    }

    /**
     * Register a handler for a specific message type.
     */
    on(type, callback) {
        this.handlers[type] = callback;
    }

    /**
     * Connect to the WebSocket server.
     * Returns a promise that resolves with the player_info data on successful auth,
     * or rejects on connection failure.
     */
    connect(playerName) {
        this._playerName = playerName;
        localStorage.setItem('ws_player_name', playerName);
        return this._createConnection(playerName);
    }

    _createConnection(playerName) {
        return new Promise((resolve, reject) => {
            try {
                const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
                const backendUrl = import.meta.env.VITE_BACKEND_URL || 'vibefishing.gulan28.com';
                const storedUUID = localStorage.getItem('ws_player_uuid');
                const reconnectParam = this._reconnecting && storedUUID ? `?reconnect_uuid=${storedUUID}` : '';
                this.ws = new WebSocket(`${wsProtocol}//${backendUrl}/ws/${playerName}${reconnectParam}`);

                this.ws.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);

                        // Handle pong immediately (don't route to handlers)
                        if (data.type === 'pong') {
                            const rtt = Math.round(performance.now() - this._pingSentAt);
                            this.ping = rtt;
                            this._pingHistory.push(rtt);
                            if (this._pingHistory.length > this._maxPingHistory) this._pingHistory.shift();
                            this._updateJitter();
                            return;
                        }

                        // Track game_tick arrival timing for jitter estimation
                        if (data.type === 'game_tick') {
                            const now = performance.now();
                            if (this._lastTickTime > 0) {
                                this._tickIntervals.push(now - this._lastTickTime);
                                if (this._tickIntervals.length > this._maxTickHistory) this._tickIntervals.shift();
                            }
                            this._lastTickTime = now;
                        }

                        // First message: player_info resolves the connect promise
                        if (data.type === 'player_info') {
                            // Store UUID for reconnection
                            localStorage.setItem('ws_player_uuid', data.uuid);
                            resolve(data);
                        }

                        // Route to registered handler
                        const handler = this.handlers[data.type];
                        if (handler) {
                            handler(data);
                        }
                    } catch (error) {
                        console.error('Error parsing message:', error);
                    }
                };

                this.ws.onopen = () => {
                    console.log('Connected to server');

                    // If this was a reconnect, reset state and notify
                    if (this._reconnecting) {
                        this._reconnecting = false;
                        this._reconnectAttempt = 0;
                        const handler = this.handlers['_reconnected'];
                        if (handler) handler();
                    }

                    // Start ping loop every 5 seconds
                    this._pingInterval = setInterval(() => {
                        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                            this._pingSentAt = performance.now();
                            this.ws.send(JSON.stringify({ type: 'ping' }));

                            // Periodically report network stats to server
                            this._jitterReportTimer += 5000;
                            if (this._jitterReportTimer >= this._jitterReportInterval) {
                                this._jitterReportTimer = 0;
                                this.reportNetworkStats();
                            }
                        }
                    }, 5000);
                };

                this.ws.onerror = (error) => {
                    console.error('WebSocket error:', error);
                    if (!this._reconnecting) {
                        reject(new Error('WebSocket connection error'));
                    }
                };

                this.ws.onclose = () => {
                    console.log('Disconnected from server');
                    if (this._pingInterval) {
                        clearInterval(this._pingInterval);
                        this._pingInterval = null;
                    }

                    if (this._intentionalClose) {
                        this._intentionalClose = false;
                        return;
                    }

                    // Start reconnection if not already reconnecting
                    if (!this._reconnecting) {
                        this._reconnecting = true;
                        this._reconnectAttempt = 0;
                        this._reconnectStartTime = Date.now();
                        const handler = this.handlers['_reconnecting'];
                        if (handler) handler();
                    }
                    this._attemptReconnect();
                };
            } catch (error) {
                console.error('Error creating WebSocket connection:', error);
                reject(error);
            }
        });
    }

    _attemptReconnect() {
        const elapsed = Date.now() - this._reconnectStartTime;
        if (elapsed >= this._maxReconnectDuration) {
            this._reconnecting = false;
            const handler = this.handlers['_disconnected'];
            if (handler) handler();
            return;
        }

        // Exponential backoff: 1s, 2s, 4s, 8s, 16s, capped at 30s
        const delay = Math.min(1000 * Math.pow(2, this._reconnectAttempt), 30000);
        this._reconnectAttempt++;
        console.log(`Reconnect attempt ${this._reconnectAttempt} in ${delay}ms`);

        this._reconnectTimer = setTimeout(() => {
            const name = this._playerName || localStorage.getItem('ws_player_name');
            if (!name) {
                this._reconnecting = false;
                const handler = this.handlers['_disconnected'];
                if (handler) handler();
                return;
            }
            this._createConnection(name);
        }, delay);
    }

    /**
     * Send a JSON message if the connection is open.
     */
    send(data) {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(data));
        }
    }

    /**
     * Convenience methods for each message type.
     */
    sendMove(position, rotation) {
        this.send({
            type: 'move',
            position: { x: position.x, y: position.y, z: position.z },
            rotation
        });
    }

    sendAnimationState(state) {
        this.send({ type: 'animation_state', state });
    }

    sendMoveTo(target, currentPos) {
        this._moveSeq = (this._moveSeq || 0) + 1;
        const msg = {
            type: 'move_to',
            target: { x: target.x, y: target.y, z: target.z },
            move_seq: this._moveSeq
        };
        if (currentPos) {
            msg.position = { x: currentPos.x, y: currentPos.y, z: currentPos.z };
        }
        this.send(msg);
        return this._moveSeq;
    }

    sendRotation(rotation) {
        this.send({ type: 'rotate', rotation });
    }

    sendCastLine(position) {
        this.send({
            type: 'cast_line',
            position: { x: position.x, y: position.y, z: position.z }
        });
    }

    sendShowFish(fish) {
        this.send({ type: 'show_fish', fish });
    }

    sendChatMessage(message) {
        this.send({ type: 'chat_message', message });
    }

    sendMinigameResult(success) {
        this.send({ type: 'minigame_result', success });
    }

    /**
     * Compute jitter from ping RTT variance and tick arrival variance.
     */
    _updateJitter() {
        // Ping-based jitter: standard deviation of recent RTTs
        let pingJitter = 0;
        if (this._pingHistory.length >= 3) {
            const mean = this._pingHistory.reduce((a, b) => a + b, 0) / this._pingHistory.length;
            const variance = this._pingHistory.reduce((sum, v) => sum + (v - mean) ** 2, 0) / this._pingHistory.length;
            pingJitter = Math.sqrt(variance);
        }

        // Tick-based jitter: deviation from expected tick interval (~33ms at 30Hz)
        let tickJitter = 0;
        if (this._tickIntervals.length >= 3) {
            const mean = this._tickIntervals.reduce((a, b) => a + b, 0) / this._tickIntervals.length;
            const variance = this._tickIntervals.reduce((sum, v) => sum + (v - mean) ** 2, 0) / this._tickIntervals.length;
            tickJitter = Math.sqrt(variance);
        }

        // Use the larger of the two as our jitter estimate
        this.jitter = Math.round(Math.max(pingJitter, tickJitter));
    }

    /**
     * Adaptive interpolation delay: scales with ping and jitter.
     */
    get interpDelay() {
        // Base: half RTT + 2x jitter, clamped between 50-300ms
        const base = this.ping * 0.5 + this.jitter * 2;
        return Math.max(50, Math.min(300, base));
    }

    /**
     * Send jitter/network stats to server for logging (called periodically).
     */
    reportNetworkStats() {
        this.send({
            type: 'network_stats',
            ping: this.ping,
            jitter: this.jitter,
            interp_delay: this.interpDelay,
            tick_intervals: this._tickIntervals.length,
            ping_samples: this._pingHistory.length
        });
    }

    get isConnected() {
        return this.ws && this.ws.readyState === WebSocket.OPEN;
    }
}
